import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Loader2, Minus, Plus } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { describeMarket } from '../../lib/kalshiTicker';
import {
  estimateAggregateFeeCents,
  takerRateForTicker,
  makerRateForTicker,
} from '../../lib/pricing/fees';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { formatMoney } from '../../lib/infra/formatters';
import { classifyCloseResult } from '../../lib/trading/closeResidual';

export interface EditPositionData {
  ticker: string;
  side: 'yes' | 'no';
  /** Full position contract count. */
  position: number;
  /** Contracts already committed to working sell orders on this (ticker, side).
   * Modal pre-fills qty to (position − pendingSellQty) and clamps the max
   * there, so a trader with 3 held / 2 already on sale defaults to selling 1. */
  pendingSellQty?: number;
  /** Side-native average entry price in cents — shown for context only. */
  averagePrice: number;
  /** Side-native current mark in cents. Used as a fallback when the live
   * orderbook hasn't streamed yet so payout/fees populate immediately. */
  currentPriceCents?: number | null;
}

interface Props {
  data: EditPositionData;
  onClose: () => void;
  onPlaced?: () => void;
}

type OrderType = 'market' | 'limit';

function StepperInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  autoFocus?: boolean;
}) {
  const numVal = parseInt(value, 10);
  const canDec = !isNaN(numVal) && (min === undefined || numVal - step >= min);
  const canInc = !isNaN(numVal) && (max === undefined || numVal + step <= max);
  return (
    <div className="bg-muted border-border flex items-center rounded-lg border">
      <button
        type="button"
        onClick={() => canDec && onChange(String(numVal - step))}
        disabled={!canDec}
        className="text-muted-foreground hover:text-foreground flex h-10 w-10 items-center justify-center transition-colors disabled:opacity-30"
      >
        <Minus size={14} />
      </button>
      <div className="relative flex-1">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          className="text-foreground w-full bg-transparent py-2 text-center font-mono text-lg focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {suffix && value && (
          <span className="text-muted-foreground pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm">
            {suffix}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          if (value === '' || isNaN(numVal)) onChange(String(min ?? step));
          else if (canInc) onChange(String(numVal + step));
        }}
        disabled={!isNaN(numVal) && !canInc}
        className="text-muted-foreground hover:text-foreground flex h-10 w-10 items-center justify-center transition-colors disabled:opacity-30"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

export function EditPositionModal({ data, onClose, onPlaced }: Props) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);
  const sweepCloseLeg = useAccountStore((s) => s.sweepCloseLeg);
  const getOrderbookRaw = useMarketStore((s) => s.getOrderbookRaw);
  const version = useMarketStore((s) => s.version);
  void version;

  const { ticker, side, position, averagePrice, currentPriceCents } = data;
  const pendingSellQty = data.pendingSellQty ?? 0;
  // Cap the editable qty at what's still uncommitted. Negative shouldn't
  // happen (more on sale than held) but clamp defensively to keep math sane.
  const availableQty = Math.max(0, position - pendingSellQty);
  const { marketLabel, betTeam } = describeMarket(ticker, side);

  // Subscribe the position's ticker to the live orderbook stream while the
  // modal is open. PositionTracker only requests books for tickers with open
  // orders; without this, opening the modal on a held position with no
  // working orders would show a stale/null book.
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('edit-position-modal', [ticker]);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('edit-position-modal', []);
    };
  }, [ticker]);

  // Best bid on the side held — that's the price you'd hit by selling at
  // market. Same-side bids are sorted defensively since hydra's level order
  // isn't guaranteed.
  const bestBidCents = useMemo(() => {
    const book = getOrderbookRaw(ticker);
    if (!book) return null;
    const levels = side === 'yes' ? book.yes : book.no;
    if (!levels || levels.length === 0) return null;
    let max = -Infinity;
    for (const lvl of levels) if (lvl.price > max) max = lvl.price;
    return Number.isFinite(max) ? max : null;
    // Re-derive on book updates; getOrderbookRaw is a stable selector ref.
  }, [getOrderbookRaw, ticker, side, version]);

  const [orderType, setOrderType] = useState<OrderType>('market');
  const [qty, setQty] = useState(String(availableQty));
  const [price, setPrice] = useState(
    bestBidCents != null ? String(bestBidCents) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Keep the limit price hint fresh until the user explicitly edits it.
  const [priceTouched, setPriceTouched] = useState(false);
  useEffect(() => {
    if (!priceTouched && bestBidCents != null && orderType === 'limit') {
      setPrice(String(bestBidCents));
    }
  }, [bestBidCents, priceTouched, orderType]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const qtyNum = parseInt(qty, 10);
  const priceNum = parseInt(price, 10);
  const canSubmit =
    !submitting &&
    !isNaN(qtyNum) &&
    qtyNum >= 1 &&
    qtyNum <= availableQty &&
    (orderType === 'market' || (!isNaN(priceNum) && priceNum >= 1 && priceNum <= 99));

  const setQtyPercent = (pct: number) => {
    const q = Math.max(1, Math.round((availableQty * pct) / 100));
    setQty(String(q));
  };

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);
    try {
      if (orderType === 'limit') {
        // Resting limit-to-close — single GTC order, no IOC residual semantics.
        // reduce_only so a stale count can't flip the position past flat.
        await placeOrder({
          ticker,
          side,
          action: 'sell',
          type: 'limit',
          count: qtyNum,
          price: priceNum,
          reduceOnly: true,
        });
        setResult({ ok: true, msg: `Resting limit to close ${qtyNum} ${side.toUpperCase()}…` });
        onPlaced?.();
        setTimeout(onClose, 900);
      } else {
        // Market close: a single marketable IOC fills what the thin book holds
        // and CANCELS the rest, silently leaving residual exposure. sweepCloseLeg
        // verifies the fill and re-sweeps only what's left, then reports any
        // residual that survives all attempts so a panic-close can't quietly
        // leave the position open.
        const residual = await sweepCloseLeg(ticker, side, qtyNum);
        onPlaced?.();
        // Three distinct outcomes — null (UNVERIFIED) and 0 (confirmed flat) must
        // NOT be conflated: only a confirmed-flat close auto-dismisses; an
        // unverifiable or partial close keeps the modal up with a loud warning.
        const outcome = classifyCloseResult(residual, { ticker, side, quantity: qtyNum });
        setResult({ ok: outcome.ok, msg: outcome.msg });
        if (outcome.autoDismiss) setTimeout(onClose, 900);
      }
    } catch (err) {
      setResult({
        ok: false,
        msg: err instanceof Error ? err.message : 'Close failed',
      });
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, placeOrder, sweepCloseLeg, ticker, side, orderType, qtyNum, priceNum, onPlaced, onClose]);

  // Fill-price assumption for the breakdown:
  //   • LIMIT  → user-entered price, fee at maker rate (resting limit).
  //   • MARKET → live best bid, fee at taker rate (crosses the spread).
  //              Falls back to the side-native current mark when the book
  //              hasn't streamed yet so the user sees real numbers instead
  //              of dashes; banner still flags the book as not loaded.
  // If the limit happens to cross at fill time, taker would actually apply.
  // We don't know that ahead of time, so we keep the maker-rate estimate
  // here and accept the optimistic skew on crossing limits.
  const marketFillCents = bestBidCents ?? currentPriceCents ?? null;
  const fillPriceCents =
    orderType === 'limit' && !isNaN(priceNum) && priceNum > 0
      ? priceNum
      : orderType === 'market'
        ? marketFillCents
        : null;
  const safeQty = qtyNum > 0 ? qtyNum : 0;

  const closeRate =
    orderType === 'limit' ? makerRateForTicker(ticker) : takerRateForTicker(ticker);
  // Aggregate per-fill ceil — the figure Kalshi's own ticket quotes (fee
  // rounded up to the cent on the whole fill). No calibration coefficient:
  // the multi-fill premium it modeled does not exist.
  const feeCents =
    fillPriceCents != null && closeRate !== null && safeQty > 0
      ? estimateAggregateFeeCents({
          priceCents: fillPriceCents,
          count: safeQty,
          feeRate: closeRate,
        })
      : null;
  // MM Program rebate isn't folded into the close (sell) ticket yet — the
  // pre-trade buy surfaces carry it. See [[project_kalshi_mm_rebate_model]].
  const makerRebateCents = 0;

  // Sell-side analogues of the BUY widgets' four-field summary so the
  // shape matches GameDetailPanel + TradeModal: Price · Total Proceeds
  // (fee) · Max Payout · Max Profit.
  //   Total Proceeds = qty × price − close fee (what you net on the sale)
  //   Max Payout     = qty × $1     (the held-to-win alternative being
  //                                  given up by selling now)
  //   Max Profit     = Total Proceeds − cost basis at avg entry
  //                    (realized P&L on the sale; pre-loss avoidance
  //                    decision, not future-tense max upside)
  const grossProceedsCents =
    fillPriceCents != null ? fillPriceCents * safeQty : null;
  const totalProceedsCents =
    grossProceedsCents == null ? null : grossProceedsCents - (feeCents ?? 0) + makerRebateCents;
  const maxPayoutCents = safeQty > 0 ? safeQty * 100 : 0;
  const costBasisCents = averagePrice * safeQty;
  const maxProfitCents =
    totalProceedsCents == null ? null : totalProceedsCents - costBasisCents;
  // American odds for Max Payout reflect the all-in proceeds-per-contract
  // implied by selling now: $1 / (proceeds_per_contract). Mirrors the
  // BUY widgets' all-in-cost odds.
  const proceedsPerContractCents =
    totalProceedsCents == null || safeQty === 0
      ? null
      : totalProceedsCents / safeQty;
  const sellOddsLabel =
    proceedsPerContractCents == null ? '—' : americanOdds(proceedsPerContractCents);

  const fmt = (c: number | null) =>
    c == null ? '—' : formatMoney(c / 100);
  const fmtSigned = (c: number | null) =>
    c == null ? '—' : formatMoney(c / 100, { showSign: true });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border-border w-[420px] rounded-lg border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-border flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-foreground text-base font-bold tracking-wide">CLOSE POSITION</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {status !== 'connected' ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Connect your Kalshi account in Settings to trade
            </p>
          ) : (
            <>
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                      side === 'yes'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {betTeam ? `(${betTeam}) ` : ''}{side.toUpperCase()}
                  </span>
                  <span className="text-foreground text-sm">
                    Holding <span className="font-mono font-bold">{position}</span> @{' '}
                    <span className="font-mono">{averagePrice}¢</span>
                  </span>
                </div>
                <p className="text-muted-foreground mt-1 font-mono text-xs">{marketLabel}</p>
              </div>

              <div className="border-border rounded-lg border px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Order Type</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOrderType('market')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderType === 'market'
                          ? 'bg-blue-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      MARKET
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderType('limit')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderType === 'limit'
                          ? 'bg-blue-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      LIMIT
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-border rounded-lg border px-4 py-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Qty</span>
                  <div className="flex items-center gap-1">
                    {[25, 50, 75, 100].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setQtyPercent(p)}
                        className="text-muted-foreground hover:text-foreground bg-muted rounded px-2 py-0.5 text-[10px] font-bold transition-colors"
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                </div>
                <StepperInput value={qty} onChange={setQty} min={1} max={availableQty} autoFocus />
                <p className="text-muted-foreground text-[11px]">
                  Max {availableQty} contracts
                  {pendingSellQty > 0 && (
                    <span className="text-amber-400 ml-1">
                      ({pendingSellQty} already on open sell{pendingSellQty === 1 ? '' : 's'})
                    </span>
                  )}
                </p>
              </div>

              {orderType === 'limit' && (
                <div className="border-border rounded-lg border px-4 py-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Price</span>
                    {bestBidCents != null && (
                      <span className="text-muted-foreground font-mono text-[11px]">
                        Best bid {bestBidCents}¢
                      </span>
                    )}
                  </div>
                  <StepperInput
                    value={price}
                    onChange={(v) => {
                      setPrice(v);
                      setPriceTouched(true);
                    }}
                    min={1}
                    max={99}
                    suffix="¢"
                  />
                </div>
              )}

              <div className="border-border rounded-lg border px-4 py-3 space-y-1.5 text-sm">
                {(() => {
                  const isMarket = orderType === 'market';
                  const tip = isMarket
                    ? 'Market orders are not guaranteed at estimated price'
                    : undefined;
                  // Market orders prefix labels with "Est." since the
                  // fill price (best bid) can move before the order
                  // routes. Limit orders are deterministic on a fill.
                  const lbl = (s: string) => (isMarket ? `Est. ${s.toLowerCase()}` : s);
                  const feeLabel =
                    feeCents == null ? '—' : formatMoney(feeCents / 100);
                  return (
                    <>
                      {/* Price · Total Proceeds (fee) · Max Payout · Max
                          Profit — sell-semantic mirror of the BUY
                          widgets' four-field shape. Same field order
                          everywhere so the trader sees the same layout. */}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground" title={tip}>
                          {lbl('Price')}
                        </span>
                        <span className="text-foreground font-mono inline-flex items-center gap-1.5">
                          <span>{fillPriceCents != null ? `${fillPriceCents}¢` : '—'}</span>
                          {fillPriceCents != null && (
                            <>
                              <span className="h-3 w-px self-center bg-current opacity-30" />
                              <span className="opacity-70">{americanOdds(fillPriceCents)}</span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground" title={tip}>
                          {lbl('Total Proceeds (fee)')}
                        </span>
                        <span className="text-foreground font-mono">
                          {fmt(totalProceedsCents)}{' '}
                          <span className="text-muted-foreground">({feeLabel})</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          className="text-muted-foreground"
                          title="Gross payout if held to win — $1 per contract. Selling now forfeits this."
                        >
                          Max Payout
                        </span>
                        <span className="text-positive font-mono inline-flex items-center gap-1.5">
                          <span>{fmt(maxPayoutCents)}</span>
                          {sellOddsLabel !== '—' && (
                            <>
                              <span className="h-3 w-px self-center bg-current opacity-30" />
                              <span className="opacity-70">{sellOddsLabel}</span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span
                          className="text-muted-foreground"
                          title="Realized P&L on the sale = Total Proceeds − cost basis at avg entry"
                        >
                          {lbl('Max Profit')}
                        </span>
                        <span
                          className={`font-mono ${
                            maxProfitCents == null
                              ? 'text-muted-foreground'
                              : maxProfitCents >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                        >
                          {fmtSigned(maxProfitCents)}
                        </span>
                      </div>
                    </>
                  );
                })()}
                {orderType === 'market' && bestBidCents == null && (
                  <p className="text-amber-400 pt-1 text-[11px]">
                    Live book not loaded — market order will fill at whatever
                    bids are present when it routes.
                  </p>
                )}
              </div>

              {result && (
                <div
                  className={`rounded-md px-3 py-2 text-xs ${
                    result.ok
                      ? 'bg-green-500/10 text-green-400'
                      : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  {result.msg}
                </div>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="bg-red-600 hover:bg-red-700 disabled:bg-muted disabled:text-muted-foreground flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  `Close ${isNaN(qtyNum) ? '' : qtyNum} ${side.toUpperCase()}`
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
